import { useState, useEffect, useCallback } from "react";
import { getSupabaseErrorMessage, isSupabaseConfigured, supabase } from "@/lib/supabaseClient";
import { Parcel } from "@/mocks/parcels";

/** Map a Supabase DB row to our Parcel interface */
function rowToParcel(row: Record<string, unknown>): Parcel {
  return {
    id: row.id as string,
    status: row.status as string,
    sender: row.sender as string,
    recipient: row.recipient as string,
    phone: row.phone as string,
    deliveryAddress: row.delivery_address as string,
    weight: row.weight as string,
    dimensions: row.dimensions as string,
    origin: row.origin as string,
    destination: row.destination as string,
    currentLocation: row.current_location as string,
    lat: Number(row.lat),
    lng: Number(row.lng),
    estimatedDelivery: row.estimated_delivery as string,
    lastUpdated: row.last_updated as string,
    timeline: (row.timeline as Parcel["timeline"]) ?? [],
  };
}

/** Map our Parcel interface to a Supabase DB row */
function parcelToRow(parcel: Parcel): Record<string, unknown> {
  return {
    id: parcel.id.trim().toUpperCase(),
    status: parcel.status,
    sender: parcel.sender,
    recipient: parcel.recipient,
    phone: parcel.phone,
    delivery_address: parcel.deliveryAddress,
    weight: parcel.weight,
    dimensions: parcel.dimensions,
    origin: parcel.origin,
    destination: parcel.destination,
    current_location: parcel.currentLocation,
    lat: parcel.lat,
    lng: parcel.lng,
    estimated_delivery: parcel.estimatedDelivery,
    last_updated: parcel.lastUpdated,
    timeline: parcel.timeline,
  };
}

export function useParcels() {
  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchParcels = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!isSupabaseConfigured || !supabase) {
      const message = getSupabaseErrorMessage();
      setError(message ?? "Supabase is not configured.");
      setParcels([]);
      setLoading(false);
      return;
    }

    const { data, error: err } = await supabase.from("parcels").select("*");

    if (err) {
      setError(err.message);
    } else {
      setParcels((data ?? []).map(rowToParcel));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchParcels();
  }, [fetchParcels]);

  /** Fetch a single parcel by tracking ID directly from Supabase */
  const getParcel = useCallback(async (id: string): Promise<Parcel | null> => {
    if (!isSupabaseConfigured || !supabase) {
      throw new Error(getSupabaseErrorMessage() ?? "Supabase is not configured.");
    }

    const normalizedId = id.trim().toUpperCase();
    const { data, error: err } = await supabase
      .from("parcels")
      .select("*")
      .eq("id", normalizedId)
      .maybeSingle();

    if (err || !data) return null;
    return rowToParcel(data as Record<string, unknown>);
  }, []);

  const addParcel = useCallback(async (parcel: Parcel): Promise<void> => {
    if (!isSupabaseConfigured || !supabase) {
      throw new Error(getSupabaseErrorMessage() ?? "Supabase is not configured.");
    }

    const row = parcelToRow(parcel);
    const { error: err } = await supabase.from("parcels").insert(row);
    if (err) throw new Error(err.message);
    await fetchParcels();
  }, [fetchParcels]);

  const updateParcel = useCallback(async (id: string, updates: Partial<Parcel>): Promise<void> => {
    if (!isSupabaseConfigured || !supabase) {
      throw new Error(getSupabaseErrorMessage() ?? "Supabase is not configured.");
    }

    const normalizedId = id.trim().toUpperCase();
    // Build only the fields that are being updated
    const rowUpdates: Record<string, unknown> = {};
    if (updates.status !== undefined) rowUpdates.status = updates.status;
    if (updates.sender !== undefined) rowUpdates.sender = updates.sender;
    if (updates.recipient !== undefined) rowUpdates.recipient = updates.recipient;
    if (updates.phone !== undefined) rowUpdates.phone = updates.phone;
    if (updates.deliveryAddress !== undefined) rowUpdates.delivery_address = updates.deliveryAddress;
    if (updates.weight !== undefined) rowUpdates.weight = updates.weight;
    if (updates.dimensions !== undefined) rowUpdates.dimensions = updates.dimensions;
    if (updates.origin !== undefined) rowUpdates.origin = updates.origin;
    if (updates.destination !== undefined) rowUpdates.destination = updates.destination;
    if (updates.currentLocation !== undefined) rowUpdates.current_location = updates.currentLocation;
    if (updates.lat !== undefined) rowUpdates.lat = updates.lat;
    if (updates.lng !== undefined) rowUpdates.lng = updates.lng;
    if (updates.estimatedDelivery !== undefined) rowUpdates.estimated_delivery = updates.estimatedDelivery;
    if (updates.lastUpdated !== undefined) rowUpdates.last_updated = updates.lastUpdated;
    if (updates.timeline !== undefined) rowUpdates.timeline = updates.timeline;

    const { error: err } = await supabase
      .from("parcels")
      .update(rowUpdates)
      .eq("id", normalizedId);

    if (err) throw new Error(err.message);
    await fetchParcels();
  }, [fetchParcels]);

  const deleteParcel = useCallback(async (id: string): Promise<void> => {
    if (!isSupabaseConfigured || !supabase) {
      throw new Error(getSupabaseErrorMessage() ?? "Supabase is not configured.");
    }

    const normalizedId = id.trim().toUpperCase();
    const { error: err } = await supabase
      .from("parcels")
      .delete()
      .eq("id", normalizedId);

    if (err) throw new Error(err.message);
    await fetchParcels();
  }, [fetchParcels]);

  return { parcels, loading, error, getParcel, addParcel, updateParcel, deleteParcel, refetch: fetchParcels };
}
